/*
 * Copyright Elasticsearch B.V. and/or licensed to Elasticsearch B.V. under one
 * or more contributor license agreements. Licensed under the Elastic License
 * 2.0 and the Server Side Public License, v 1; you may not use this file except
 * in compliance with, at your election, the Elastic License 2.0 or the Server
 * Side Public License, v 1.
 */

import React, { Component } from 'react';
import PropTypes from "prop-types";
import classNames from 'classnames';
import { EuiButton } from '../../button';
import { EuiI18n } from '../../i18n';
import { EuiToolTip } from '../../tool_tip';
import { EuiHideFor, EuiShowFor } from '../../responsive';
export class EuiSuperUpdateButton extends Component {
  static defaultProps = {
    needsUpdate: false,
    isLoading: false,
    isDisabled: false,
    showTooltip: false,
    responsive: ['xs', 's'],
    fill: true
  };
  _isMounted = false;
  tooltipTimeout;
  tooltip = null;
  componentWillUnmount() {
    this._isMounted = false;
  }
  componentDidMount() {
    this._isMounted = true;
  }
  componentDidUpdate() {
    if (this.props.showTooltip && this.props.needsUpdate && !this.props.isDisabled && !this.props.isLoading) {
      this.showTooltip();
      this.tooltipTimeout = setTimeout(() => {
        this.hideTooltip();
      }, 2000);
    }
  }
  setTootipRef = node => {
    this.tooltip = node;
  };
  showTooltip = () => {
    if (!this._isMounted || !this.tooltip) {
      return;
    }
    this.tooltip.showToolTip();
  };
  hideTooltip = () => {
    if (!this._isMounted || !this.tooltip) {
      return;
    }
    this.tooltip.hideToolTip();
  };
  render() {
    const {
      className,
      needsUpdate,
      isLoading,
      isDisabled,
      onClick,
      toolTipProps,
      showTooltip,
      iconOnly,
      responsive: _responsive,
      textProps: restTextProps,
      fill,
      ...rest
    } = this.props;
    // Force responsive for "all" if `iconOnly = true`
    const responsive = iconOnly ? 'all' : _responsive;
    const classes = classNames('euiSuperUpdateButton', className);
    let buttonText = <EuiI18n token="euiSuperUpdateButton.refreshButtonLabel" default="Refresh" />;
    if (needsUpdate || isLoading) {
      buttonText = isLoading ? <EuiI18n token="euiSuperUpdateButton.updatingButtonLabel" default="Updating" /> : <EuiI18n token="euiSuperUpdateButton.updateButtonLabel" default="Update" />;
    }
    let tooltipContent;
    if (isDisabled) {
      tooltipContent = <EuiI18n token="euiSuperUpdateButton.cannotUpdateTooltip" default="Cannot update" />;
    } else if (needsUpdate && !isLoading) {
      tooltipContent = <EuiI18n token="euiSuperUpdateButton.clickToApplyTooltip" default="Click to apply" />;
    }
    const sharedButtonProps = {
      color: needsUpdate || isLoading ? 'success' : 'primary',
      iconType: needsUpdate || isLoading ? 'kqlFunction' : 'refresh',
      isDisabled: isDisabled,
      onClick: onClick,
      isLoading: isLoading
    };
    return <EuiToolTip ref={this.setTootipRef} content={tooltipContent} position="bottom" {...toolTipProps}>
        <>
          <EuiShowFor sizes={responsive || 'none'}>
            <EuiButton className={classes} minWidth={false} {...sharedButtonProps} fill={fill} textProps={{
            ...restTextProps,
            className: classNames('euiScreenReaderOnly', restTextProps?.className)
          }} {...rest}>
              {buttonText}
            </EuiButton>
          </EuiShowFor>
          <EuiHideFor sizes={responsive || 'none'}>
            <EuiButton className={classes} minWidth={118} {...sharedButtonProps} fill={fill} textProps={restTextProps} {...rest}>
              {buttonText}
            </EuiButton>
          </EuiHideFor>
        </>
      </EuiToolTip>;
  }
}
EuiSuperUpdateButton.propTypes = {
  isDisabled: PropTypes.bool,
  isLoading: PropTypes.bool,
  needsUpdate: PropTypes.bool,
  onClick: PropTypes.func.isRequired,
  /**
     * Show the "Click to apply" tooltip
     */
  showTooltip: PropTypes.bool,
  /**
     * Passes props to `EuiToolTip`
     */
  toolTipProps: PropTypes.shape({
    /**
       * Passes onto the span wrapping the trigger.
       */
    anchorClassName: PropTypes.string,
    /**
       * Passes onto the span wrapping the trigger.
       */
    anchorProps: PropTypes.shape({
      className: PropTypes.string,
      "aria-label": PropTypes.string,
      "data-test-subj": PropTypes.string,
      css: PropTypes.any
    }),
    /**
       * The in-view trigger for your tooltip.
       */
    children: PropTypes.element.isRequired,
    /**
       * Passes onto the tooltip itself, not the trigger.
       */
    className: PropTypes.string,
    /**
       * The main content of your tooltip.
       */
    content: PropTypes.node,
    /**
       * Common display alternatives for the anchor wrapper
       */
    display: PropTypes.any,
    /**
       * Delay before showing tooltip. Good for repeatable items.
       */
    delay: PropTypes.oneOf(["regular", "long"]).isRequired,
    /**
       * An optional title for your tooltip.
       */
    title: PropTypes.node,
    /**
       * Unless you provide one, this will be randomly generated.
       */
    id: PropTypes.string,
    /**
       * Suggested position. If there is not enough room for it this will be changed.
       */
    position: PropTypes.oneOf(["top", "right", "bottom", "left"]).isRequired,
    /**
       * When `true`, the tooltip's position is re-calculated when the user
       * scrolls. This supports having fixed-position tooltip anchors.
       *
       * When nesting an `EuiTooltip` in a scrollable container, `repositionOnScroll` should be `true`
       */
    repositionOnScroll: PropTypes.bool,
    /**
       * If supplied, called when mouse movement causes the tool tip to be
       * hidden.
       */
    onMouseOut: PropTypes.func,
    "aria-label": PropTypes.string,
    "data-test-subj": PropTypes.string,
    css: PropTypes.any
  }),
  /**
     * Returns an IconButton instead
     */
  iconOnly: PropTypes.bool,
  /**
     * Forces state to be `iconOnly` when within provided breakpoints.
     * Remove completely with `false` or provide your own list of breakpoints.
     */
  responsive: PropTypes.oneOfType([PropTypes.oneOf([false]), PropTypes.arrayOf(PropTypes.any.isRequired).isRequired])
};
try {
  EuiSuperUpdateButton.__docgenInfo = {
    tags: {},
    filePath: '/app/src/components/date_picker/super_date_picker/super_update_button.tsx',
    description: '',
    displayName: 'EuiSuperUpdateButton',
    methods: [],
    props: {
      isDisabled: {
        defaultValue: {
          value: 'false'
        },
        description: '',
        name: 'isDisabled',
        parent: undefined,
        declarations: [{
          fileName: 'app/src/components/date_picker/super_date_picker/super_update_button.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'boolean'
        }
      },
      isLoading: {
        defaultValue: {
          value: 'false'
        },
        description: '',
        name: 'isLoading',
        parent: undefined,
        declarations: [{
          fileName: 'app/src/components/date_picker/super_date_picker/super_update_button.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'boolean'
        }
      },
      needsUpdate: {
        defaultValue: {
          value: 'false'
        },
        description: '',
        name: 'needsUpdate',
        parent: undefined,
        declarations: [{
          fileName: 'app/src/components/date_picker/super_date_picker/super_update_button.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'boolean'
        }
      },
      onClick: {
        defaultValue: null,
        description: '',
        name: 'onClick',
        parent: undefined,
        declarations: [{
          fileName: 'app/src/components/date_picker/super_date_picker/super_update_button.tsx',
          name: 'TypeLiteral'
        }],
        required: true,
        type: {
          name: 'MouseEventHandler<HTMLButtonElement>'
        }
      },
      showTooltip: {
        defaultValue: {
          value: 'false'
        },
        description: 'Show the "Click to apply" tooltip',
        name: 'showTooltip',
        parent: undefined,
        declarations: [{
          fileName: 'app/src/components/date_picker/super_date_picker/super_update_button.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'boolean'
        }
      },
      toolTipProps: {
        defaultValue: null,
        description: 'Passes props to `EuiToolTip`',
        name: 'toolTipProps',
        parent: undefined,
        declarations: [{
          fileName: 'app/src/components/date_picker/super_date_picker/super_update_button.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'EuiToolTipProps'
        }
      },
      iconOnly: {
        defaultValue: null,
        description: 'Returns an IconButton instead',
        name: 'iconOnly',
        parent: undefined,
        declarations: [{
          fileName: 'app/src/components/date_picker/super_date_picker/super_update_button.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'boolean'
        }
      },
      responsive: {
        defaultValue: {
          value: "['xs', 's']"
        },
        description: 'Forces state to be `iconOnly` when within provided breakpoints.\n' + 'Remove completely with `false` or provide your own list of breakpoints.',
        name: 'responsive',
        parent: undefined,
        declarations: [{
          fileName: 'app/src/components/date_picker/super_date_picker/super_update_button.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'false | string[]'
        }
      },
      color: {
        defaultValue: null,
        description: 'Any of the named color palette options.\n' + "**`'ghost'` is set for deprecation. Use EuiThemeProvide.colorMode = 'dark' instead.**",
        name: 'color',
        parent: {
          fileName: 'app/src/components/button/button.tsx',
          name: 'BaseProps'
        },
        declarations: [{
          fileName: 'app/src/components/button/button.tsx',
          name: 'BaseProps'
        }],
        required: false,
        type: {
          name: 'enum',
          raw: 'EuiButtonColor',
          value: [{
            value: '"text"'
          }, {
            value: '"accent"'
          }, {
            value: '"primary"'
          }, {
            value: '"success"'
          }, {
            value: '"warning"'
          }, {
            value: '"danger"'
          }, {
            value: '"ghost"'
          }]
        }
      },
      className: {
        defaultValue: null,
        description: '',
        name: 'className',
        parent: {
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        },
        declarations: [{
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      style: {
        defaultValue: null,
        description: '',
        name: 'style',
        parent: {
          fileName: 'app/src/components/button/button_display/_button_display.tsx',
          name: 'EuiButtonDisplayCommonProps'
        },
        declarations: [{
          fileName: 'app/src/components/button/button_display/_button_display.tsx',
          name: 'EuiButtonDisplayCommonProps'
        }],
        required: false,
        type: {
          name: 'CSSProperties'
        }
      },
      'aria-label': {
        defaultValue: null,
        description: '',
        name: 'aria-label',
        parent: {
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        },
        declarations: [{
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      'data-test-subj': {
        defaultValue: null,
        description: '',
        name: 'data-test-subj',
        parent: {
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        },
        declarations: [{
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      css: {
        defaultValue: null,
        description: '',
        name: 'css',
        parent: {
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        },
        declarations: [{
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        }],
        required: false,
        type: {
          name: 'Interpolation<Theme>'
        }
      },
      size: {
        defaultValue: null,
        description: 'Use size `s` in confined spaces',
        name: 'size',
        parent: {
          fileName: 'app/src/components/button/button.tsx',
          name: 'BaseProps'
        },
        declarations: [{
          fileName: 'app/src/components/button/button.tsx',
          name: 'BaseProps'
        }],
        required: false,
        type: {
          name: 'enum',
          raw: '"s" | "m"',
          value: [{
            value: '"s"'
          }, {
            value: '"m"'
          }]
        }
      },
      element: {
        defaultValue: null,
        description: '',
        name: 'element',
        parent: {
          fileName: 'app/src/components/button/button_display/_button_display.tsx',
          name: 'EuiButtonDisplayCommonProps'
        },
        declarations: [{
          fileName: 'app/src/components/button/button_display/_button_display.tsx',
          name: 'EuiButtonDisplayCommonProps'
        }],
        required: false,
        type: {
          name: 'enum',
          raw: '"a" | "button" | "label" | "span"',
          value: [{
            value: '"a"'
          }, {
            value: '"button"'
          }, {
            value: '"label"'
          }, {
            value: '"span"'
          }]
        }
      },
      isSelected: {
        defaultValue: null,
        description: 'Applies the boolean state as the `aria-pressed` property to create a toggle button.\n' + '*Only use when the readable text does not change between states.*',
        name: 'isSelected',
        parent: {
          fileName: 'app/src/components/button/button_display/_button_display.tsx',
          name: 'EuiButtonDisplayCommonProps'
        },
        declarations: [{
          fileName: 'app/src/components/button/button_display/_button_display.tsx',
          name: 'EuiButtonDisplayCommonProps'
        }],
        required: false,
        type: {
          name: 'boolean'
        }
      },
      fullWidth: {
        defaultValue: null,
        description: 'Extends the button to 100% width',
        name: 'fullWidth',
        parent: {
          fileName: 'app/src/components/button/button_display/_button_display.tsx',
          name: 'EuiButtonDisplayCommonProps'
        },
        declarations: [{
          fileName: 'app/src/components/button/button_display/_button_display.tsx',
          name: 'EuiButtonDisplayCommonProps'
        }],
        required: false,
        type: {
          name: 'boolean'
        }
      },
      minWidth: {
        defaultValue: null,
        description: 'Override the default minimum width',
        name: 'minWidth',
        parent: {
          fileName: 'app/src/components/button/button_display/_button_display.tsx',
          name: 'EuiButtonDisplayCommonProps'
        },
        declarations: [{
          fileName: 'app/src/components/button/button_display/_button_display.tsx',
          name: 'EuiButtonDisplayCommonProps'
        }],
        required: false,
        type: {
          name: 'false | MinWidth<string | number>'
        }
      },
      contentProps: {
        defaultValue: null,
        description: "Object of props passed to the <span/> wrapping the button's content",
        name: 'contentProps',
        parent: {
          fileName: 'app/src/components/button/button_display/_button_display.tsx',
          name: 'EuiButtonDisplayCommonProps'
        },
        declarations: [{
          fileName: 'app/src/components/button/button_display/_button_display.tsx',
          name: 'EuiButtonDisplayCommonProps'
        }],
        required: false,
        type: {
          name: 'CommonProps & HTMLAttributes<HTMLSpanElement>'
        }
      },
      iconType: {
        defaultValue: null,
        description: 'Any `type` accepted by EuiIcon',
        name: 'iconType',
        parent: {
          fileName: 'app/src/components/button/button_display/_button_display_content.tsx',
          name: 'EuiButtonDisplayContentProps'
        },
        declarations: [{
          fileName: 'app/src/components/button/button_display/_button_display_content.tsx',
          name: 'EuiButtonDisplayContentProps'
        }],
        required: false,
        type: {
          name: 'IconType'
        }
      },
      iconSide: {
        defaultValue: null,
        description: 'Can only be one side `left` or `right`',
        name: 'iconSide',
        parent: {
          fileName: 'app/src/components/button/button_display/_button_display_content.tsx',
          name: 'EuiButtonDisplayContentProps'
        },
        declarations: [{
          fileName: 'app/src/components/button/button_display/_button_display_content.tsx',
          name: 'EuiButtonDisplayContentProps'
        }],
        required: false,
        type: {
          name: 'enum',
          raw: 'ButtonContentIconSide',
          value: [{
            value: '"left"'
          }, {
            value: '"right"'
          }]
        }
      },
      textProps: {
        defaultValue: null,
        description: "Object of props passed to the <span/> wrapping the content's text/children only (not icon)",
        name: 'textProps',
        parent: {
          fileName: 'app/src/components/button/button_display/_button_display_content.tsx',
          name: 'EuiButtonDisplayContentProps'
        },
        declarations: [{
          fileName: 'app/src/components/button/button_display/_button_display_content.tsx',
          name: 'EuiButtonDisplayContentProps'
        }],
        required: false,
        type: {
          name: "HTMLAttributes<HTMLSpanElement> & CommonProps & { ref?: Ref<HTMLSpanElement>; 'data-text'?: string; }"
        }
      },
      iconSize: {
        defaultValue: null,
        description: '',
        name: 'iconSize',
        parent: {
          fileName: 'app/src/components/button/button_display/_button_display_content.tsx',
          name: 'EuiButtonDisplayContentProps'
        },
        declarations: [{
          fileName: 'app/src/components/button/button_display/_button_display_content.tsx',
          name: 'EuiButtonDisplayContentProps'
        }],
        required: false,
        type: {
          name: 'enum',
          raw: '"s" | "m"',
          value: [{
            value: '"s"'
          }, {
            value: '"m"'
          }]
        }
      },
      fill: {
        defaultValue: {
          value: 'true'
        },
        description: 'Make button a solid color for prominence',
        name: 'fill',
        parent: {
          fileName: 'app/src/components/button/button.tsx',
          name: 'BaseProps'
        },
        declarations: [{
          fileName: 'app/src/components/button/button.tsx',
          name: 'BaseProps'
        }],
        required: false,
        type: {
          name: 'boolean'
        }
      }
    },
    extendedInterfaces: ['BaseProps', 'EuiButtonDisplayCommonProps', 'CommonProps', 'EuiButtonDisplayContentProps']
  };
} catch (e) {}